import { useStoredState } from "hooks/useStoredState";
import { joinRoom } from "socket/events/joinRoom";
import styles from "./styles.module.scss";
import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Form,
  Icon,
  Menu,
  Section,
} from "react-bulma-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShuffle } from "@fortawesome/free-solid-svg-icons";
import { NameInput } from "components/NameInput/NameInput";
import { Header } from "components/Header/Header";
import { getRandomRoomName } from "utils/getRandomRoomName";
import { Background } from "./Background/Background";
import { Footer } from "components/Footer/Footer";
import { Page } from "components/Page/Page";
import { store } from "store/store";
import { observer } from "mobx-react-lite";
import { Link } from "wouter";
import { useT } from "i18n/I18nProvider";
import {
  START_NEXT_STEP_KEYS,
  START_QUICK_RULE_KEYS,
  START_STEP_KEYS,
} from "i18n/content";

export const StartGame = observer(() => {
  const t = useT();
  const [roomName, setRoomName] = useStoredState<string>("roomNameInput", "");
  const [isPublic, setIsPublic] = useStoredState<boolean>("isPublic", true);
  useEffect(() => {
    if (window.location.hash) {
      setRoomName(window.location.hash.substring(1));
    }
  }, [setRoomName]);
  const randomRoomNameForPlaceholder = useMemo(() => getRandomRoomName(), []);

  const openRooms = store.openRooms;
  const hasOpenRooms = openRooms.length > 0;

  return (
    <>
      <div className={styles.mainContainer}>
        <Header />
        <Page withFooter>
          <div className={styles.startPage}>
            <Background />
            <div className={styles.leftSide}>
              <div className={styles.heroPanel}>
                <p className={styles.eyebrow}>{t("start.eyebrow")}</p>
                <h1 className={styles.title}>{t("start.title")}</h1>
                <p className={styles.description}>{t("start.description")}</p>

                <div className={styles.infoGrid}>
                  <section className={styles.infoCard}>
                    <h2>{t("start.howToStart")}</h2>
                    <ol className={styles.list}>
                      {START_STEP_KEYS.map((stepKey) => (
                        <li key={stepKey}>{t(stepKey)}</li>
                      ))}
                    </ol>
                  </section>

                  <section className={styles.infoCard}>
                    <h2>{t("start.whatHappensNext")}</h2>
                    <ul className={styles.list}>
                      {START_NEXT_STEP_KEYS.map((stepKey) => (
                        <li key={stepKey}>{t(stepKey)}</li>
                      ))}
                    </ul>
                  </section>
                </div>

                <section className={styles.infoCard}>
                  <h2>{t("start.quickRules")}</h2>
                  <ul className={styles.list}>
                    {START_QUICK_RULE_KEYS.map((ruleKey) => (
                      <li key={ruleKey}>{t(ruleKey)}</li>
                    ))}
                  </ul>
                  <div className={styles.rulesRow}>
                    <Link href="/rules" className={styles.rulesLink}>
                      {t("start.readFullRules")}
                    </Link>
                    <span className={styles.rulesNote}>
                      {t("start.rulesNote")}
                    </span>
                  </div>
                </section>
              </div>
            </div>
            <div className={styles.rightSide}>
              <Section>
                <Container className={styles.container}>
                  <Box className={styles.formCard}>
                    <div className={styles.formIntro}>
                      <h2>{t("start.form.title")}</h2>
                      <p>{t("start.form.description")}</p>
                    </div>
                    <Form.Field>
                      <Form.Label>{t("start.roomCode")}</Form.Label>
                      <Form.Field kind="addons">
                        <Form.Input
                          autoFocus
                          placeholder={t("start.roomExample", {
                            example: randomRoomNameForPlaceholder,
                          })}
                          value={roomName}
                          onChange={(e) => setRoomName(e.target.value)}
                          onKeyDown={(event) => {
                            if (event.code === "Enter" && roomName) {
                              joinRoom(roomName, !!isPublic);
                            }
                          }}
                        />
                        <Form.Control>
                          <Button
                            color="primary"
                            onClick={() => {
                              const randomRoomName = getRandomRoomName();
                              setRoomName(randomRoomName);
                            }}
                          >
                            <Icon>
                              <FontAwesomeIcon icon={faShuffle} />
                            </Icon>
                          </Button>
                        </Form.Control>
                      </Form.Field>
                      <p className={styles.helpText}>{t("start.roomHelp")}</p>
                    </Form.Field>
                    <Form.Field>
                      <Form.Control>
                        <Form.Checkbox
                          checked={isPublic}
                          onChange={(e) => setIsPublic(e.target.checked)}
                        >
                          {t("start.publicRoom")}
                        </Form.Checkbox>
                      </Form.Control>
                      <p className={styles.helpText}>
                        {t("start.publicRoomHelp")}
                      </p>
                    </Form.Field>
                    <NameInput
                      onKeyDown={(event) => {
                        if (event.code === "Enter" && roomName) {
                          joinRoom(roomName, !!isPublic);
                        }
                      }}
                    />
                    <p className={styles.helpText}>{t("start.nameHelp")}</p>
                    <Button
                      fullwidth
                      color="primary"
                      disabled={!roomName}
                      onClick={() => joinRoom(roomName, !!isPublic)}
                    >
                      {t("start.play")}
                    </Button>
                    <p className={styles.playHint}>
                      {roomName
                        ? t("start.playHint.ready")
                        : t("start.playHint.empty")}
                    </p>
                  </Box>
                </Container>
              </Section>
              {hasOpenRooms && (
                <Section>
                  <Container className={styles.container}>
                    <Box className={styles.openRoomsCard}>
                      <div className={styles.formIntro}>
                        <h2>{t("start.openRooms.title")}</h2>
                        <p>{t("start.openRooms.description")}</p>
                      </div>
                      <Menu>
                        {openRooms.map((room) => (
                          <Menu.List key={room.name}>
                            <Menu.List.Item
                              onClick={() => {
                                joinRoom(room.name);
                              }}
                              className={styles.openRoomItem}
                            >
                              <span>{room.name}</span>
                              <small>
                                {t("start.openRooms.players", {
                                  count: room.playersCount,
                                })}
                              </small>
                            </Menu.List.Item>
                          </Menu.List>
                        ))}
                      </Menu>
                    </Box>
                  </Container>
                </Section>
              )}
            </div>
          </div>
        </Page>
        <Footer />
      </div>
    </>
  );
});
